import DataTable from "react-data-table-component";
import Pagination from "./Pagination";
import styles from "./Table.module.scss";
import { combineClasses } from "@/lib/utils";
import TableSkeleton from "./TableSkeleton";
import { useSelector } from "react-redux";
import type { Props } from "@/lib/constants";
import NoData from "./NoData";
import type { RootState } from "@/store/rootReducer";
import CustomHeader from "@/components/CustomHeader/CustomHeader";
import { renderAutoTooltipCell, shouldAutoTooltip } from "@/components/Tooltip";

const Table = <T,>(props: Props<T>) => {
  const { sidebarOpen } = useSelector((state: RootState) => state.global);
  const { customStyles: _customStyles, paginationProps, containerHeight, header, ...dataTableProps } = props;

  const processedColumns = props.columns?.map((col) => ({
    ...col,
    name: col.icon ? (
      <div className={styles.headerWithIcon}>
        {col.icon}
        <span>{col.name}</span>
      </div>
    ) : (
      col.name
    ),
    // Plain `selector` columns get an only-when-truncated tooltip (see
    // `ColumnTooltipOption`). `selector` itself is kept for sorting. A
    // `format` column already controls its own rendering, so it's skipped.
    ...(shouldAutoTooltip(col) &&
      !col.format && {
        cell: (row: T, rowIndex: number) =>
          renderAutoTooltipCell(col.selector?.(row, rowIndex), {
            tooltip: col.tooltip,
            align: col.center ? "center" : col.right ? "right" : undefined,
            // Mirrors react-data-table's own rule for which cells are clickable.
            allowRowEvents: !col.ignoreRowClick && !col.button,
          }),
      }),
  }));

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: containerHeight || "calc(100vh - 100px)",
        overflow: "hidden",
      }}
    >
      <div className={styles.tableWrapper}>
        {header && (
          <div className={styles.tableHeader}>
            <CustomHeader {...header} />
          </div>
        )}
        <DataTable
          {...dataTableProps}
          columns={processedColumns || []}
          pagination={false}
          noDataComponent={<NoData columns={props.columns || []} sidebarOpen={sidebarOpen} />}
          customStyles={{
            cells: {
              style: {
                color: "rgb(var(--table-text))",
              },
            },
          }}
          fixedHeaderScrollHeight="100%"
          className={combineClasses("scrollable", styles.table, sidebarOpen ? styles.sidebarOpen : "", props.className)}
          progressComponent={<TableSkeleton columns={props.columns || []} sidebarOpen={sidebarOpen} />}
          fixedHeader
          responsive
        />

        {paginationProps?.isPagination && (
          <div className={styles.paginationWrapper}>
            <Pagination
              totalCount={paginationProps.totalCount}
              pageSelected={paginationProps.pageSelected}
              rowsPerPageValue={paginationProps.rowsPerPageValue}
              setPageSelected={paginationProps.setPageSelected ?? (() => {})}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Table;
